import React, { useMemo, useState } from 'react';
import { CheckCircle, AlertTriangle, Ban } from 'lucide-react';
import type { ComponentWithReplacement } from '../types';
import type { Building } from '../../../../types/protocols';
import { componentMediaList } from '../utils/componentMedia';
import ComponentListMedia, { COLLAPSED_COUNT } from './ComponentListMedia';

/** Massredigering: the item is ticked instead of opened */
export interface ListItemSelection {
    checked: boolean;
    /** Another specificType than the selection is locked to */
    disabled: boolean;
    onToggle: () => void;
}

interface ComponentListItemProps {
    plan: ComponentWithReplacement;
    building: Building;
    isSelected: boolean;
    isDone: boolean;
    isExcluded: boolean;
    hasIncompleteInfo: boolean;
    onClick: () => void;
    selection?: ListItemSelection;
}

/**
 * One component in the draft's list. In Enskild a tap opens it. In
 * Massredigering only the checkbox ticks it; the row carries the component's
 * pictures (ComponentListMedia) and a tap anywhere else on it expands or
 * collapses them.
 */
const ComponentListItem: React.FC<ComponentListItemProps> = ({
    plan,
    building,
    isSelected,
    isDone,
    isExcluded,
    hasIncompleteInfo,
    onClick,
    selection,
}) => {
    const [expanded, setExpanded] = useState(false);
    const inBulkMode = !!selection;
    const media = useMemo(
        () => (inBulkMode ? componentMediaList(plan.component, building) : []),
        [inBulkMode, plan.component, building]
    );
    const canExpand = media.length > COLLAPSED_COUNT;

    const classNames = [
        'component-list-item',
        isSelected && 'selected',
        isDone && 'done',
        isExcluded && 'excluded',
        hasIncompleteInfo && !isDone && !isExcluded && 'incomplete',
        selection && 'selectable',
        selection?.disabled && 'unselectable',
        media.length > 0 && 'with-media',
        selection && canExpand && 'expandable',
    ].filter(Boolean).join(' ');

    const name = plan.component.displayName(building);
    const handleClick = selection
        ? () => { if (canExpand) setExpanded(open => !open); }
        : onClick;

    return (
        <div
            className={classNames}
            onClick={handleClick}
            title={selection?.disabled ? 'Komponenten är av en annan typ än de valda' : undefined}
        >
            <div className="component-list-item-row">
                {selection && (
                    <input
                        type="checkbox"
                        className="form-check-input component-list-item-check"
                        checked={selection.checked}
                        disabled={selection.disabled}
                        onChange={selection.onToggle}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Välj ${name}`}
                    />
                )}
                <span className="component-list-item-name">
                    {name}
                </span>
                <div className="component-list-item-status">
                    {isDone && <CheckCircle size={14} className="status-icon text-success" />}
                    {isExcluded && <Ban size={14} className="status-icon text-muted" style={{ transform: 'scaleX(-1)' }} />}
                    {hasIncompleteInfo && !isDone && !isExcluded && (
                        <AlertTriangle size={14} className="status-icon text-warning" />
                    )}
                </div>
            </div>
            {media.length > 0 && (
                <ComponentListMedia
                    media={media}
                    expanded={expanded}
                    onToggleExpanded={() => setExpanded(open => !open)}
                />
            )}
        </div>
    );
};

export default ComponentListItem;
